// Body composition from skinfolds (mm). The legacy DB labels the equation "SIRI", but the stored value
// is a direct percent-fat equation of the skinfold sum. Spec: NGF-formulas-verified-v2.md § Body composition.
import { round } from './num.ts';
import type { ProvisionalRule, Sex } from './types.ts';

export interface Skinfolds {
  chest?: number | null;
  triceps?: number | null;
  subscapular?: number | null;
  abdomen?: number | null;
  suprailiac?: number | null;
  thigh?: number | null;
}

const MALE_SITES = ['chest', 'triceps', 'subscapular', 'abdomen', 'suprailiac', 'thigh'] as const;
const FEMALE_SITES = ['triceps', 'subscapular', 'abdomen', 'suprailiac', 'thigh'] as const;

export const requiredSites = (sex: Sex) => (sex === 'M' ? MALE_SITES : FEMALE_SITES);

/** Percent body fat (unrounded), or null if any required site is missing. */
export function bodyFatPct(sex: Sex, age: number, sf: Skinfolds): number | null {
  const vals = requiredSites(sex).map((k) => sf[k]);
  if (vals.some((v) => v === null || v === undefined || !(v > 0))) return null;
  const s = (vals as number[]).reduce((a, b) => a + b, 0);
  // Male: v2 spec, 96.6 % exact / 100 % within 0.1 of 111K archived values.
  // Female: v2's least-squares coefficients matched only 65 %; these were tuned 2026-09-22 to maximize
  // exact matches (94 % of 9.8K). The published source equation is still unidentified → provisional.
  return sex === 'M'
    ? 0.215478 * s - 0.00029 * s ** 2 + 0.133176 * age - 5.71372
    : 0.29692 * s - 0.000529 * s ** 2 + 0.030765 * age - 0.046437;
}

// Derived 2026-09-22 from stored ideal weights (lean / (1 − target), 99.5 % of 41K rows); matches the
// packet's printed "14–20 %" for a 23-year-old man. (The v2 spec's 15/16/17/18/18 table was wrong.)
const TARGET = { M: [14, 15, 17, 18, 19], F: [20, 21, 22, 23, 24] } as const;

/**
 * Target % fat for the age band (< 30, 30s, 40s, 50s, 60+), banded by age rounded to whole years like
 * the risk tables. The report's target range is target … target + 6.
 */
export function targetFatPct(sex: Sex, age: number): number {
  const band = Math.min(4, Math.max(0, Math.trunc(round(age) / 10) - 2));
  return TARGET[sex][band]!;
}

/** Weight at the target % fat keeping lean mass (lb, 0.01). */
export const targetWeightLb = (leanLb: number, targetPct: number): number =>
  round((leanLb * 100) / (100 - targetPct), 2);

/** Essential-fat floor below which the report flags "hazardous". */
export const hazardFatPct = (sex: Sex): number => (sex === 'M' ? 5 : 9);

/** A body-fat goal: % fat, total weight and fat weight to aim for (lean mass kept), and days to get there. */
export interface FatGoal {
  fatPct: number;      // 0.1
  weightLb: number;    // 0.01
  fatWeightLb: number; // 0.01
  days: number;        // from the test date
}

/** Short-term goal: lose up to this much fat, at about a pound a week (as ePhysioLogic did). */
export const FAT_LOSS_STEP_LB = 12;
export const DAYS_PER_LB = 7;

/**
 * Goals when % fat is above target: the target itself, and a short-term step of at most 12 lb of fat.
 * Null when already at or below target ("maintain"). Rules from the legacy data (2023+): given the
 * target % the old program used, target weight matches 97 %, short-term fat weight 83 %, dates ±1 day.
 * The old program took its target % from a per-computer setting (this table, or one step higher), so
 * the goals use the same target the report already shows. Provisional until the MD confirms.
 */
export function fatGoals(fatPct: number, fatWeightLb: number, leanLb: number, targetPct: number): { shortTerm: FatGoal; target: FatGoal } | null {
  if (fatPct <= targetPct) return null;
  const tWeight = targetWeightLb(leanLb, targetPct);
  const tFat = round(tWeight - leanLb, 2);
  const sFat = Math.max(round(fatWeightLb - FAT_LOSS_STEP_LB, 2), tFat);
  const sWeight = round(leanLb + sFat, 2);
  const days = (fat: number) => Math.round(DAYS_PER_LB * (fatWeightLb - fat));
  return {
    shortTerm: { fatPct: round((100 * sFat) / sWeight, 1), weightLb: sWeight, fatWeightLb: sFat, days: days(sFat) },
    target: { fatPct: targetPct, weightLb: tWeight, fatWeightLb: tFat, days: days(tFat) },
  };
}

export interface BodyCompInput {
  sex: Sex;
  age: number;
  weightLb: number;
  skinfolds: Skinfolds;
}

export interface BodyCompResult {
  fatPct: number; // 0.1
  fatWeightLb: number; // 0.01 (legacy values can differ by a few hundredths)
  leanWeightLb: number; // 0.1
  targetFatPct: number;
  targetRange: [number, number];
  targetWeightLb: number; // 0.01
  hazardFatPct: number;
  /** Null when at or below target. (Absent on results stored before engine 0.2.0.) */
  goals?: { shortTerm: FatGoal; target: FatGoal } | null;
  provisional: ProvisionalRule[];
}

/** Null when skinfolds were not recorded — the body composition page is then omitted. */
export function scoreBodyComposition(x: BodyCompInput): BodyCompResult | null {
  const raw = bodyFatPct(x.sex, x.age, x.skinfolds);
  if (raw === null || !(x.weightLb > 0)) return null;
  // Weights use the unrounded % fat. Lean is kept at 0.1 lb and target weight is derived from that
  // rounded lean as lean × 100 / (100 − target%), which keeps ties like 93.875 exact so they round up
  // as the legacy program did (target weight: M 100 %, F 98.7 % of 2017+ rows; lean/(1 − 0.20) would
  // land on 93.87499… and round down).
  const fatWt = (x.weightLb * raw) / 100;
  const lean = round(x.weightLb - fatWt, 1);
  const target = targetFatPct(x.sex, x.age);
  const goals = fatGoals(round(raw, 1), round(fatWt, 2), lean, target);
  return {
    fatPct: round(raw, 1),
    fatWeightLb: round(fatWt, 2),
    leanWeightLb: lean,
    targetFatPct: target,
    targetRange: [target, target + 6],
    targetWeightLb: targetWeightLb(lean, target),
    hazardFatPct: hazardFatPct(x.sex),
    goals,
    provisional: [...(x.sex === 'F' ? ['body-fat-female' as const] : []), ...(goals ? ['body-fat-goals' as const] : [])],
  };
}
